import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { UserInput } from "@core/types";
import * as api from "./api";
import type { UserActiveFilter, UserCreateInput } from "./api";

const listKey = (search: string, profileId: string, active: UserActiveFilter) =>
  ["portal", "security-users", search, profileId, active] as const;

export function useUsers(
  search: string,
  profileId: string,
  active: UserActiveFilter,
) {
  return useInfiniteQuery({
    queryKey: listKey(search, profileId, active),
    queryFn: ({ pageParam }) =>
      api.listUsers({ search, profileId, active, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.next ?? undefined,
  });
}

export const useProfiles = () =>
  useQuery({
    queryKey: ["portal", "security-profiles"],
    queryFn: api.listProfiles,
  });

export const useActivePlaces = (catalog: api.PlaceCatalog) =>
  useQuery({
    queryKey: ["portal", "catalog-entries", catalog, "active"],
    queryFn: () => api.listActivePlaces(catalog),
  });

export function useUserMutations(
  search: string,
  profileId: string,
  active: UserActiveFilter,
) {
  const client = useQueryClient();
  const invalidate = () =>
    client.invalidateQueries({ queryKey: listKey(search, profileId, active) });
  return {
    create: useMutation({
      mutationFn: (input: UserCreateInput) => api.createUser(input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: UserInput }) =>
        api.editUser(id, input),
      onSuccess: invalidate,
    }),
    inactivate: useMutation({
      mutationFn: (id: string) => api.inactivateUser(id),
      onSuccess: invalidate,
    }),
    reactivate: useMutation({
      mutationFn: (id: string) => api.reactivateUser(id),
      onSuccess: invalidate,
    }),
    resetPassword: useMutation({
      mutationFn: (id: string) => api.resetUserPassword(id),
    }),
  };
}
